/**
 * PrefetchLink
 *
 * A drop-in replacement for React Router's <Link> that starts fetching
 * the destination page's data the moment the user HOVERS or FOCUSES the
 * link — before they even click.
 *
 * By the time the click registers, the data is already in the cache and
 * the page renders instantly (0ms perceived lag).
 *
 * Usage:
 *   <PrefetchLink to="/shop"    prefetch="products">SHOP</PrefetchLink>
 *   <PrefetchLink to="/product/1" prefetch="product" prefetchId={1}>
 *     View Product
 *   </PrefetchLink>
 *
 * Props:
 *   prefetch   — which dataset to warm (see PREFETCH_MAP below)
 *   prefetchId — numeric id for per-resource keys (product, reviews)
 *   All other props are forwarded to <Link>.
 */

import React, { useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { KEYS } from '../../hooks/useForgeQueries';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const get = (path) =>
  fetch(`${API}${path}`, { cache: 'no-store' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} — ${path}`);
    return r.json();
  });

/**
 * Maps a prefetch name → { queryKey, queryFn }
 * Add more entries here as you create new pages.
 */
const PREFETCH_MAP = {
  products:       (id) => ({ queryKey: KEYS.allProducts,        queryFn: () => get('/allproducts')        }),
  // Shares KEYS.allProducts, not KEYS.product(id) — useProduct() reads
  // from that same key, so this actually warms the cache it will use.
  product:        (id) => ({ queryKey: KEYS.allProducts,        queryFn: () => get('/allproducts')        }),
  newCollections: (id) => ({ queryKey: KEYS.newCollections,     queryFn: () => get('/newcollections')     }),
  packs:          (id) => ({ queryKey: KEYS.packs,              queryFn: () => get('/packs')              }),
  comingSoon:     (id) => ({ queryKey: KEYS.comingSoon,         queryFn: () => get('/comingsoon')         }),
  reviews:        (id) => ({ queryKey: KEYS.reviews(id),        queryFn: () => get(`/reviews/${id}`)     }),
};

const PrefetchLink = ({
  to,
  prefetch,      // keyof PREFETCH_MAP
  prefetchId,    // optional numeric id (product id, review productId)
  children,
  onMouseEnter,
  onFocus,
  ...rest
}) => {
  const queryClient = useQueryClient();
  const didPrefetch = useRef(false); // fire only once per mount

  const triggerPrefetch = useCallback(() => {
    if (!prefetch || didPrefetch.current) return;
    const entry = PREFETCH_MAP[prefetch]?.(prefetchId);
    if (!entry) return;
    didPrefetch.current = true;

    queryClient.prefetchQuery({
      queryKey:  entry.queryKey,
      queryFn:   entry.queryFn,
      // Only prefetch if the cache is older than 30 seconds
      // (avoids redundant network calls on rapid re-hovers)
      staleTime: 30 * 1000,
    });
  }, [queryClient, prefetch, prefetchId]);

  return (
    <Link
      to={to}
      onMouseEnter={(e) => { triggerPrefetch(); onMouseEnter?.(e); }}
      onFocus={(e)      => { triggerPrefetch(); onFocus?.(e);      }}
      {...rest}
    >
      {children}
    </Link>
  );
};

export default PrefetchLink;
